// ProjectsPage — workspaces that gather the chats (and, through them, artifacts) for one piece of work.
// List table (name/desc/chats/artifacts/last-active) + New project (opens it) + inline rename +
// delete-with-Undo (deleting a project never deletes its chats/artifacts). Studio Cyan/Stone tokens.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Snackbar from '@mui/material/Snackbar'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import FolderIcon from '@mui/icons-material/Folder'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useRosShell } from '../RosShellContext'
import { listProjects, createProject, updateProject, deleteProject, type ProjectListItem } from './projectsApi'
import { relativeModified } from './artifactsModel'
import { SERIF_HEADING } from '../theme'

const UNDO_MS = 5000

export function ProjectsPage() {
  const shell = useRosShell()
  const navigate = useNavigate()
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [menu, setMenu] = useState<{ anchor: HTMLElement; project: ProjectListItem } | null>(null)
  const [query, setQuery] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const suppressBlurRef = useRef(false)
  const [pendingDelete, setPendingDelete] = useState<ProjectListItem | null>(null)
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Create dialog (name + optional description) — replaces the old silent "Untitled project" create.
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [creating, setCreating] = useState(false)

  const reload = useCallback(async () => {
    try { setProjects(await listProjects(shell.apiBaseUrl, shell.token)) }
    catch { /* empty state */ }
    finally { setLoading(false) }
  }, [shell.apiBaseUrl, shell.token])

  useEffect(() => { void reload() }, [reload])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return projects.filter(p =>
      p.id !== pendingDelete?.id &&
      (!q || p.name.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q)),
    )
  }, [projects, pendingDelete, query])

  const openCreate = () => { setNewName(''); setNewDesc(''); setCreateOpen(true) }
  const submitCreate = async () => {
    const name = newName.trim()
    if (!name || creating) return
    setCreating(true)
    try {
      const id = await createProject(shell.apiBaseUrl, shell.token, name, newDesc.trim() || undefined)
      setCreateOpen(false)
      navigate(`/projects/${id}`)
    } catch { /* keep dialog open on failure */ }
    finally { setCreating(false) }
  }

  const startRename = (p: ProjectListItem) => {
    setEditValue(p.name)
    suppressBlurRef.current = true
    setEditingId(p.id)
    setMenu(null)
  }
  const commitRename = async () => {
    if (!editingId) return
    const name = editValue.trim()
    const id = editingId
    setEditingId(null)
    if (!name) return
    setProjects(prev => prev.map(p => p.id === id ? { ...p, name } : p))
    try { await updateProject(shell.apiBaseUrl, shell.token, id, { name }) } catch { void reload() }
  }
  const onFieldBlur = () => {
    if (suppressBlurRef.current) { suppressBlurRef.current = false; return }
    void commitRename()
  }

  // The server delete is DEFERRED until the Undo window closes. The pending row lives in a ref (not
  // just state) so the unmount-only flush below can read it WITHOUT depending on `pendingDelete` —
  // depending on it would re-run the effect's cleanup on every change, firing the delete the moment
  // the user clicks Undo (which sets pendingDelete back to null). Ref + empty-dep = flush on unmount only.
  const pendingRef = useRef<ProjectListItem | null>(null)
  useEffect(() => { pendingRef.current = pendingDelete }, [pendingDelete])

  const confirmAndDelete = (p: ProjectListItem) => {
    setMenu(null)
    // One Undo at a time: flush any prior still-pending delete before starting this one.
    if (commitTimer.current) clearTimeout(commitTimer.current)
    if (pendingRef.current) void deleteProject(shell.apiBaseUrl, shell.token, pendingRef.current.id)
    setPendingDelete(p)
    commitTimer.current = setTimeout(() => {
      void deleteProject(shell.apiBaseUrl, shell.token, p.id).then(reload)
      setPendingDelete(null)
    }, UNDO_MS)
  }
  const undoDelete = () => {
    if (commitTimer.current) clearTimeout(commitTimer.current)
    setPendingDelete(null)   // row reappears (it was only filtered from `visible`, never deleted)
  }
  // Flush a still-pending delete only on real unmount (empty deps; reads the ref, not state).
  useEffect(() => () => {
    if (commitTimer.current) clearTimeout(commitTimer.current)
    if (pendingRef.current) void deleteProject(shell.apiBaseUrl, shell.token, pendingRef.current.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const GRID = '1fr 90px 110px 150px 60px'

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', px: 4, py: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h3" sx={SERIF_HEADING}>Projects</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 640 }}>
            Workspaces that gather the chats and artifacts for one piece of work. Everything here also lives in your Chats and Artifacts.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0, mt: 0.5 }}>New project</Button>
      </Box>

      {/* Search + count — mirrors the Chats list page. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <TextField
          size="small" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search projects"
          sx={{ width: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.8rem' }}>
          {visible.length} {visible.length === 1 ? 'project' : 'projects'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: 'primary.main' }} /></Box>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 6, textAlign: 'center' }}>
          <Box sx={{ width: 48, height: 48, mx: 'auto', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,143,168,0.10)', color: 'primary.main' }}><FolderIcon /></Box>
          <Typography sx={{ fontWeight: 600, mt: 1.5 }}>{query.trim() ? `No projects match “${query.trim()}”` : 'No projects yet'}</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 0.5 }}>{query.trim() ? 'Try a different search.' : 'Create one to group related chats and artifacts in a single place.'}</Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: GRID, px: 2.5, py: 1, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
            {['Project', 'Chats', 'Artifacts', 'Last active', ''].map((h, i) => (
              <Typography key={i} sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem', textAlign: i === 1 || i === 2 ? 'center' : 'left' }}>{h}</Typography>
            ))}
          </Box>
          {visible.map((p, i) => (
            <Box
              key={p.id}
              onClick={() => { if (editingId !== p.id) navigate(`/projects/${p.id}`) }}
              sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', px: 2.5, py: 1.6, cursor: editingId === p.id ? 'default' : 'pointer', borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, '&:hover .row-more': { opacity: 1 } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,143,168,0.10)', color: 'primary.main' }}><FolderIcon sx={{ fontSize: 20 }} /></Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {editingId === p.id ? (
                    <TextField
                      autoFocus size="small" value={editValue}
                      onClick={e => e.stopPropagation()}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={onFieldBlur}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); void commitRename() }
                        else if (e.key === 'Escape') { suppressBlurRef.current = false; setEditingId(null) }
                      }}
                      sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: 999, fontWeight: 600, fontSize: '0.9rem' }, '& .MuiOutlinedInput-input': { py: 0.6 } }}
                    />
                  ) : (
                    <>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{p.name}</Typography>
                      {p.description && <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>{p.description}</Typography>}
                    </>
                  )}
                </Box>
              </Box>
              <Typography sx={{ textAlign: 'center', fontSize: '0.9rem' }}>{p.chatCount}</Typography>
              <Typography sx={{ textAlign: 'center', fontSize: '0.9rem' }}>{p.artifactCount}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{relativeModified(new Date(p.lastActive).toISOString())}</Typography>
              <Box sx={{ justifySelf: 'end' }}>
                <IconButton className="row-more" size="small" onClick={e => { e.stopPropagation(); setMenu({ anchor: e.currentTarget, project: p }) }} sx={{ opacity: 0, color: 'text.secondary', transition: 'opacity 0.15s' }}><MoreVertIcon fontSize="small" /></IconButton>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)} disableRestoreFocus anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => menu && startRename(menu.project)}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menu && confirmAndDelete(menu.project)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete project</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create dialog — name (required) + description (optional). */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={SERIF_HEADING}>New project</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            autoFocus label="Name" fullWidth size="small" value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) { e.preventDefault(); void submitCreate() } }}
            placeholder="e.g. Coffee Co — Brand Tracker"
          />
          <TextField
            label="Description" fullWidth size="small" multiline minRows={2} value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            placeholder="Optional — a short line about this project"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={() => void submitCreate()} disabled={!newName.trim() || creating} sx={{ textTransform: 'none', fontWeight: 600 }}>Create project</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!pendingDelete} message={`Deleted “${pendingDelete?.name}”.`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={<Button color="primary" size="small" onClick={undoDelete}>Undo</Button>}
      />
    </Box>
  )
}
